import { type Condition } from '@aphp-ema/common/conditions';
import type { Question, QuestionOption } from '@aphp-ema/common/questionnaire';

export type BrokenCondition = {
  questionId: string;
  reason: 'type_change' | 'option_removed' | 'question_deleted' | 'reordering';
  condition: Condition;
};

const findQuestionById = (
  questions: Question[],
  questionId: string,
): Question | undefined => {
  return questions.find((q) => q.id === questionId);
};

const getConditionsTargetingQuestion = (
  question: Question,
  targetQuestionId: string,
): Condition[] => {
  const conditions = question.displayConditions?.conditions || [];
  return conditions.filter(
    (condition) =>
      condition.target?.source === 'question' &&
      condition.target.questionId === targetQuestionId,
  );
};

const createBrokenCondition = (
  questionId: string,
  reason: 'type_change' | 'option_removed' | 'question_deleted' | 'reordering',
  condition: Condition,
): BrokenCondition => ({
  questionId,
  reason,
  condition,
});

const checkConditionForRemovedOption = (
  condition: Condition,
  removedOptions: string[],
): boolean => {
  if (
    'value' in condition &&
    typeof condition.value === 'string' &&
    removedOptions.includes(condition.value)
  ) {
    return true;
  }

  return false;
};

export const checkQuestionTypeChangeConditions = (
  questions: Question[],
  changedQuestionId: string,
): BrokenCondition[] => {
  const brokenConditions: BrokenCondition[] = [];
  const changedQuestion = findQuestionById(questions, changedQuestionId);

  if (!changedQuestion) return brokenConditions;

  questions.forEach((question) => {
    const targetingConditions = getConditionsTargetingQuestion(
      question,
      changedQuestionId,
    );

    targetingConditions.forEach((condition) => {
      // Always consider type changes as breaking conditions, even if operators are compatible
      // This ensures users are aware of any type changes to questions used in conditions
      if (
        !brokenConditions.some(
          (b) => b.questionId === question.id && b.condition === condition,
        )
      ) {
        brokenConditions.push(
          createBrokenCondition(question.id, 'type_change', condition),
        );
      }
    });
  });

  return brokenConditions;
};

export const checkQuestionOptionsChangeConditions = (
  questions: Question[],
  changedQuestionId: string,
  removedOptions: string[],
  changedOptions: string[] = [],
): BrokenCondition[] => {
  if (removedOptions.length === 0 && changedOptions.length === 0) return [];

  const brokenConditions: BrokenCondition[] = [];

  questions.forEach((question) => {
    const targetingConditions = getConditionsTargetingQuestion(
      question,
      changedQuestionId,
    );

    targetingConditions.forEach((condition) => {
      // Check for removed options
      const isRemovedOption = checkConditionForRemovedOption(
        condition,
        removedOptions,
      );

      // Check for changed options (labels that exist in both old and new but with different values)
      const isChangedOption = checkConditionForRemovedOption(
        condition,
        changedOptions,
      );

      if (
        (isRemovedOption || isChangedOption) &&
        !brokenConditions.some(
          (b) => b.questionId === question.id && b.condition === condition,
        )
      ) {
        brokenConditions.push(
          createBrokenCondition(question.id, 'option_removed', condition),
        );
      }
    });
  });

  return brokenConditions;
};

export const getRemovedOptions = (
  oldOptions: QuestionOption[] | null,
  newOptions: QuestionOption[] | null,
): string[] => {
  if (!oldOptions) return [];
  if (!newOptions) return oldOptions.map((opt) => opt.label);

  const newLabels = new Set(newOptions.map((opt) => opt.label));
  return oldOptions
    .filter((opt) => !newLabels.has(opt.label))
    .map((opt) => opt.label);
};

export const getChangedOptions = (
  oldOptions: QuestionOption[] | null,
  newOptions: QuestionOption[] | null,
): string[] => {
  if (!oldOptions || !newOptions) return [];

  // Find options that exist at the same index but have different labels
  const changedLabels: string[] = [];

  const minLength = Math.min(oldOptions.length, newOptions.length);
  for (let i = 0; i < minLength; i++) {
    if (oldOptions[i].label !== newOptions[i].label) {
      // Add the old label as it's the one that might be referenced in conditions
      changedLabels.push(oldOptions[i].label);
    }
  }

  return changedLabels;
};

export const checkQuestionDeletionConditions = (
  questions: Question[],
  deletedQuestionId: string,
): BrokenCondition[] => {
  const brokenConditions: BrokenCondition[] = [];

  questions.forEach((question) => {
    const targetingConditions = getConditionsTargetingQuestion(
      question,
      deletedQuestionId,
    );

    targetingConditions.forEach((condition) => {
      brokenConditions.push(
        createBrokenCondition(question.id, 'question_deleted', condition),
      );
    });
  });

  return brokenConditions;
};

export const checkAllowMultipleChangeConditions = (
  questions: Question[],
  changedQuestionId: string,
  newAllowMultiple: boolean,
): BrokenCondition[] => {
  if (newAllowMultiple) return [];

  const brokenConditions: BrokenCondition[] = [];

  questions.forEach((question) => {
    const targetingConditions = getConditionsTargetingQuestion(
      question,
      changedQuestionId,
    );

    targetingConditions.forEach((condition) => {
      if (
        condition.operator === 'contains' ||
        condition.operator === 'not_contains'
      ) {
        brokenConditions.push(
          createBrokenCondition(question.id, 'type_change', condition),
        );
      }
    });
  });

  return brokenConditions;
};
